import "./style.scss";

import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Tooltip,
} from "@mui/material";
import ModalBox from "components/Modal";
import { isManagementBankInfoComplete } from "helpers/paymentExportUtils";
import { formatNumber } from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import type { IHandleRequest } from "hooks/useAPIPagination";
import type { ITermBeneficiar } from "store/managementStore";
import { useSelectRows } from "hooks/useSelectRow";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchBeneficiar } from "store/beneficiaryStore";
import { EPaymentResourceType } from "store/paymentSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { PeriodStatus } from "store/types";
import type { TSortOrder } from "types";

import colors from "../../../assets/styles/_variables.module.scss";
import variables from "../../../assets/styles/_variables.module.scss";
import EmptyState from "../EmptyState";
import ManagementTableAuthorComponent from "../ManagementTableAuthorComponent";
import ModalBase from "../ModalBase";
import Pagination from "../Pagination";
import { Spinner } from "../Spinner";
import type { INewPaymentData } from "../PaymentModal";
import PaymentModal from "../PaymentModal";
import StyledCheckbox from "../StyledCheckbox";
import type { WhiteDropdownMenuOptions } from "../WhiteDropdownMenu";
import { WhiteDropdownMenu } from "../WhiteDropdownMenu";
import BeneficiaryStatement from "./BeneficiaryStatement";
import WithheldModal from "./Modal/WithheldModal";

type TProps = {
    page: number;
    count: number;
    limitPagination: number;
    sortBy: string;
    sortOrder: TSortOrder;
    handleChangePage: (event: unknown, newPage: number) => void;
    handleChangeLimit: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSort: (name: string) => void;
    handleRequest: (params?: IHandleRequest) => void;
    selectedRows: number[];
    setSelectedRows: React.Dispatch<React.SetStateAction<number[]>>;
    /**
     * Handle search query
     */
    handleQuery: (e: React.ChangeEvent<HTMLInputElement>) => void;
    periodStatus?: PeriodStatus;
    isRoyaltiesRecalculating?: boolean;
};
const tHeadStyles = {
    maxWidth: 200,
    fontWeight: 400,
    fontSize: "12px",
    color: "#676767",
    width: "min-content",
    padding: "8px 20px",
    backgroundColor: "#F6F6F6",
    fontFamily: "'Poppins', sans-serif",
};

function ManagementBeneficiariesTable({
    page,
    count,
    limitPagination,
    sortBy,
    sortOrder,
    handleChangePage,
    handleChangeLimit,
    handleSort,
    handleRequest,
    selectedRows,
    setSelectedRows,
    handleQuery,
    periodStatus,
    isRoyaltiesRecalculating = false,
}: TProps) {
    const dispatch = useAppDispatch();
    const [t] = useTranslation();

    const isPeriodFuture = periodStatus === PeriodStatus.FUTURE;

    const { beneficiaries, benefInfoLoadingStatus } = useAppSelector((state) => state.management);
    const { countryFeatures, internationalBankingEnabled } = useCompanySettings();
    const { activePeriod, activePeriodData } = useAppSelector((state) => state.period);
    const { beneficiar: beneficiary } = useAppSelector((state) => state.beneficiary);
    const [beneficiariesInfo, setBeneficiariesInfo] = useState(beneficiaries);
    const [elementsForPayment, setElementsForPayments] = useState<INewPaymentData[]>([]);

    const [modalType, setModalType] = useState<string | null>(null);
    const [paymentType, setPaymentType] = useState<EPaymentResourceType>(EPaymentResourceType.BENEFICIARY);
    const [currentBeneficiaryTerm, setCurrentBeneficiaryTerm] = useState<ITermBeneficiar | null>(null);
    const { allSelected, setAllSelected, handleSelectRow } = useSelectRows({
        setSelectedRows,
        data: beneficiariesInfo,
    });

    const handleSetAllSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.checked) {
            setAllSelected(true);
            setSelectedRows(beneficiariesInfo.map((benef) => benef.id));
        } else {
            setAllSelected(false);
            setSelectedRows([]);
        }
    };

    useEffect(() => {
        if (currentBeneficiaryTerm && currentBeneficiaryTerm.id !== beneficiary.id)
            dispatch(fetchBeneficiar(currentBeneficiaryTerm.id));
    }, [currentBeneficiaryTerm]);

    useEffect(() => {
        setBeneficiariesInfo(beneficiaries);
    }, [beneficiaries]);

    useEffect(() => {
        setAllSelected(false);
    }, [setAllSelected]);

    const showEmptyState =
        !beneficiariesInfo.length &&
        !beneficiaries.length &&
        activePeriod !== undefined &&
        benefInfoLoadingStatus === "succeeded" &&
        !isRoyaltiesRecalculating;

    const isLoading =
        !beneficiaries.length ||
        !beneficiariesInfo.length ||
        benefInfoLoadingStatus !== "succeeded" ||
        isRoyaltiesRecalculating;

    if (showEmptyState) {
        return <EmptyState type="beneficiary" sx={{ margin: "auto" }} />;
    }

    /**
     * Opens payment modal and sets info such as left to pay and id for each beneficiary selected.
     */
    const handleOpenPaymentModal = (_beneficiary: ITermBeneficiar | null, type: EPaymentResourceType) => {
        if (selectedRows.length === 0 && _beneficiary) {
            const leftToPay = _beneficiary?.stageAndCommitted
                ? _beneficiary.netToPay - _beneficiary?.stageAndCommitted
                : _beneficiary.netToPay;

            const bankInfoValid = isManagementBankInfoComplete(
                _beneficiary,
                countryFeatures.bankingFields,
                internationalBankingEnabled,
            );
            const elementsData: INewPaymentData[] = [
                {
                    amount: leftToPay ?? 0,
                    id: _beneficiary?.id,
                    completingPayment: leftToPay < _beneficiary.netToPay && leftToPay > 0,
                    bankInfoValid,
                    name: `${_beneficiary.surname} ${_beneficiary.name ?? ""}`,
                    underTheMinimum: _beneficiary.underTheMinimum,
                },
            ];
            setElementsForPayments(elementsData);
            setPaymentType(type);
            setModalType("payment");
        }
    };

    const menuOptions: WhiteDropdownMenuOptions[] = [
        {
            id: 1,
            name: t("PartialPayment.Actions.PartiallyPaid"),
            hide: (currentBeneficiaryTerm?.netToPay ?? 0) - (currentBeneficiaryTerm?.stageAndCommitted ?? 0) === 0,
            action: () => handleOpenPaymentModal(currentBeneficiaryTerm, EPaymentResourceType.PARTIAL_PAYMENT),
            disabled: isPeriodFuture,
        },
        {
            id: 2,
            name: t("Management.ShowStatement"),
            action: () => {
                setModalType("statement");
            },
        },
    ];

    /**
     * Checks if full amount of royalties is in Paid/For transfer or both.
     */
    const checkIfBeneficiaryPaid = (beneficiary: ITermBeneficiar) => {
        const amountLeftToPay = beneficiary.netToPay - beneficiary.stagedAmount - beneficiary.paidAmount;
        return amountLeftToPay < 0 || beneficiary.netToPay === beneficiary.stageAndCommitted;
    };

    const disablePaymentBtnTooltip = (beneficiary: ITermBeneficiar): string => {
        if (checkIfBeneficiaryPaid(beneficiary)) {
            return t("PartialPayment.AllPaid");
        }
        // Diable if has carried forward royalties
        if (beneficiary.underTheMinimum && !activePeriodData?.isActive) {
            return t("PartialPayment.HasCarriedForwardRoyalties");
        }
        return "";
    };

    return (
        <>
            {currentBeneficiaryTerm && (
                <BeneficiaryStatement
                    setIsOpen={setModalType}
                    statementCurrency={beneficiary.statementCurrency}
                    isOpen={modalType === "statement"}
                    currentBeneficiary={currentBeneficiaryTerm}
                />
            )}

            <ModalBox
                open={modalType === "withheld"}
                onClose={() => {
                    setModalType("");
                }}
                mainTitle={t("Management.WithheldCurrentPeriod")}
                subTitle={`${currentBeneficiaryTerm?.surname?.toUpperCase()} ${currentBeneficiaryTerm?.name}`}
            >
                <WithheldModal beneficiary={currentBeneficiaryTerm!} />
            </ModalBox>
            <ModalBase
                open={modalType === "payment"}
                onClose={() => setModalType("")}
                modalTitle={t("PartialPayment.PaymentModalTitle", { name: currentBeneficiaryTerm?.beneficiaryName })}
            >
                <PaymentModal
                    elementsData={elementsForPayment}
                    type={paymentType}
                    periodId={activePeriod}
                    handleClose={() => setModalType("")}
                    context="management-pay"
                />
            </ModalBase>
            <Paper
                sx={{
                    padding: 0,
                    width: "100%",
                    // overflow: "hidden",
                    marginBottom: "20px",
                    border: `1px solid ${colors["border-grey"]}`,
                    borderRadius: colors["border-raduis"],
                    boxShadow: "none",
                }}
            >
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell
                                    sx={{
                                        padding: "8px 10px",
                                        backgroundColor: "#F6F6F6",
                                        textAlign: "center",
                                    }}
                                >
                                    <StyledCheckbox
                                        disabled={isPeriodFuture}
                                        onChange={handleSetAllSelected}
                                        checked={allSelected}
                                        size="small"
                                    />
                                </TableCell>
                                <TableCell sx={tHeadStyles}>
                                    <TableSortLabel
                                        active={sortBy === "beneficiaryName"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("beneficiaryName")}
                                    >
                                        {t("Management.Author")}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="right">
                                    <TableSortLabel
                                        active={sortBy === "grossDuty"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("grossDuty")}
                                    >
                                        {t("Management.GrossDuty")}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="right">
                                    <TableSortLabel
                                        active={sortBy === "netToPay"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("netToPay")}
                                    >
                                        {t("Management.NetDutiesToBePaid")}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell
                                    sx={tHeadStyles}
                                    align="right"
                                    style={{ borderLeft: `1px solid ${variables["border-grey"]}` }}
                                >
                                    <TableSortLabel
                                        active={sortBy === "paidAmount"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("paidAmount")}
                                    >
                                        <div
                                            className="status-dot green"
                                            style={{ display: "flex", alignItems: "center" }}
                                        >
                                            <span>{t("PartialPayment.Status.Paid")}</span>
                                        </div>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="right">
                                    <TableSortLabel
                                        active={sortBy === "stagedAmount"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("stagedAmount")}
                                    >
                                        <div
                                            className="status-dot orange"
                                            style={{ display: "flex", alignItems: "center" }}
                                        >
                                            <span>{t("PartialPayment.Status.ForTransfer")}</span>
                                        </div>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="right">
                                    <TableSortLabel
                                        active={sortBy === "notPaid"}
                                        direction={sortOrder.toLowerCase() as "asc" | "desc"}
                                        onClick={() => handleSort("unpaidAmount")}
                                    >
                                        <div
                                            className="status-dot red"
                                            style={{ display: "flex", alignItems: "center" }}
                                        >
                                            <span>{t("PartialPayment.Status.Unpaid")}</span>
                                        </div>
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell sx={tHeadStyles} align="right"></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!isLoading ? (
                                beneficiariesInfo.map((beneficiary, index) => {
                                    const amountLeftToPay =
                                        beneficiary.netToPay - beneficiary.stagedAmount - beneficiary.paidAmount;
                                    return (
                                        <TableRow
                                            tabIndex={-1}
                                            key={`${beneficiary.id}-${index}`}
                                            className="benef-table-row"
                                            onClick={() => {
                                                setModalType("statement");
                                                setCurrentBeneficiaryTerm(beneficiary);
                                            }}
                                            sx={{
                                                backgroundColor: selectedRows.includes(beneficiary.id)
                                                    ? "#FAF9FF"
                                                    : "white",
                                                position: "relative",
                                            }}
                                        >
                                            <TableCell
                                                sx={{
                                                    textAlign: "center",
                                                    padding: "10px",
                                                }}
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <StyledCheckbox
                                                    disabled={isPeriodFuture}
                                                    onChange={(e) => handleSelectRow(e, beneficiary.id)}
                                                    checked={selectedRows.includes(beneficiary.id)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell sx={{ padding: "8px 0 8px 8px" }}>
                                                <ManagementTableAuthorComponent
                                                    beneficiaryId={beneficiary.id}
                                                    author={`${beneficiary.surname.toUpperCase()} ${
                                                        beneficiary.name ?? ""
                                                    }`}
                                                    lastSentAt={beneficiary.lastSentAt}
                                                    avatarUrl={beneficiary.avatarUrl}
                                                    handleQuery={handleQuery}
                                                    page="management"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <span
                                                    style={{
                                                        color: `${
                                                            (beneficiary as any).underTheMinimum ? "#B55D5D" : ""
                                                        }`,
                                                    }}
                                                >
                                                    {formatNumber(Math.round(beneficiary.grossDuty) / 100, {
                                                        isCurrency: true,
                                                        always2Digit: beneficiary.grossDuty > 0,
                                                    })}
                                                </span>
                                            </TableCell>
                                            <TableCell align="right">
                                                <span
                                                    className={`box ${(beneficiary as any).underTheMinimum && "red"}`}
                                                >
                                                    {formatNumber(Math.round(beneficiary.netToPay) / 100, {
                                                        isCurrency: true,
                                                        always2Digit: beneficiary.netToPay > 0,
                                                    })}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                align="right"
                                                style={{
                                                    borderLeft: "2px solid var(--border-color)",
                                                    backgroundColor: "#fafafa",
                                                }}
                                            >
                                                <span>
                                                    {beneficiary.paidAmount > 0
                                                        ? formatNumber(Math.round(beneficiary.paidAmount) / 100, {
                                                              isCurrency: true,
                                                              always2Digit: beneficiary.paidAmount > 0,
                                                          })
                                                        : "-"}
                                                </span>
                                            </TableCell>
                                            <TableCell align="right" style={{ backgroundColor: "#fafafa" }}>
                                                <span>
                                                    {beneficiary.stagedAmount > 0
                                                        ? formatNumber(Math.round(beneficiary.stagedAmount) / 100, {
                                                              isCurrency: true,
                                                              always2Digit: beneficiary.stagedAmount > 0,
                                                          })
                                                        : "-"}
                                                </span>
                                            </TableCell>
                                            <TableCell align="right" style={{ backgroundColor: "#fafafa" }}>
                                                <Tooltip title={amountLeftToPay < 0 ? "Excédent payé" : ""} arrow>
                                                    <span>
                                                        {formatNumber(Math.round(amountLeftToPay) / 100, {
                                                            isCurrency: true,
                                                            always2Digit: amountLeftToPay > 0,
                                                        })}
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell
                                                align="center"
                                                onClick={(e) => e.stopPropagation()}
                                                style={{ backgroundColor: "#fafafa" }}
                                            >
                                                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                                                    <WhiteDropdownMenu
                                                        options={menuOptions}
                                                        style={{ backgroundColor: "white" }}
                                                        buttonName={t("PartialPayment.Actions.Pay")}
                                                        onMenuClick={() => {
                                                            setCurrentBeneficiaryTerm(beneficiary);
                                                        }}
                                                        mainAction={() =>
                                                            handleOpenPaymentModal(
                                                                beneficiary,
                                                                EPaymentResourceType.BENEFICIARY,
                                                            )
                                                        }
                                                        disableMainAction={
                                                            isPeriodFuture ||
                                                            selectedRows.length > 0 ||
                                                            !!disablePaymentBtnTooltip(beneficiary)
                                                        }
                                                        tooltipText={
                                                            isPeriodFuture
                                                                ? t("Management.FuturePeriodDescription")
                                                                : disablePaymentBtnTooltip(beneficiary)
                                                        }
                                                    />
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Spinner size={36} />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component={() => (
                        <Pagination
                            rowsPerPageOptions={[10, 25, 100]}
                            rowsPerPage={limitPagination}
                            currentPageSize={beneficiariesInfo.length}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeLimit}
                            type="beneficiaries"
                            count={count}
                            sticky={true}
                        />
                    )}
                    count={-1}
                    rowsPerPage={limitPagination}
                    page={page}
                    onPageChange={handleChangePage}
                />
            </Paper>
        </>
    );
}

export default ManagementBeneficiariesTable;
